import {
  Component,
  Input,
  OnDestroy,
  OnInit,
  ChangeDetectorRef,
} from '@angular/core';
import { Component as BrComponent, Page } from '@bloomreach/spa-sdk/';
import { NavigationService } from '@frk/eds-components';
import {
  AppStateService,
  ProfileService,
  StockDataDTO,
  StockQuoteListResultDTO,
  StockTickerServiceService,
  WidenService,
} from '@services';
import { LinkService } from '@services/link.service';
import { CardContent } from '@types';
import { Logger } from '@utils/logger';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { symbols } from '@utils/il8n/currency';

import { TranslateService } from '@shared/translate/translate.service';

/**
 * Component Logger
 */
const logger = Logger.getLogger('BannerMultiPromoComponent');

/**
 * Promo Card Interface
 */
interface PromoCardDetails {
  title?: string;
  subTitle?: string;
  imageSrc?: string;
  linkBtnLabel?: string;
  linkBtnHref?: string;
  isNewWindow?: boolean;
  externalLink?: boolean;
  signInRequired?: boolean;
  stockPrice?: string;
  currency?: string;
  stockAdditionalContent?: string;
  hasThumbnail?: boolean;
  hasStockPrice?: boolean;
  theme?: string;
}

@Component({
  selector: 'ft-banner-multi-promo',
  templateUrl: './banner-multi-promo.component.html',
})
export class BannerMultiPromoComponent implements OnInit, OnDestroy {
  /**
   * The Bloomreach component
   */
  @Input() component!: BrComponent;

  /**
   * The Bloomreach page
   */
  @Input() page!: Page;

  /**
   * Object to be passed to EDS component
   */
  promoCards: PromoCardDetails[] = [];

  marginBottom: string;

  stockTicker: boolean;

  stockFeed: StockQuoteListResultDTO;

  isLoggedIn: boolean;

  private unsubscribe$: Subject<void> = new Subject<void>();

  /**
   * Constructor
   * @param widenService injected widen service
   */
  constructor(
    private appStateService: AppStateService,
    private linkService: LinkService,
    private widenService: WidenService,
    private navigationService: NavigationService,
    private profileService: ProfileService,
    private stockTickerServiceService: StockTickerServiceService,
    private translateService: TranslateService,
    private cdr: ChangeDetectorRef
  ) {
    this.profileService
      .isLoggedIn$()
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((isLoggedIn) => {
        this.isLoggedIn = isLoggedIn;
      });
  }

  /**
   * On Init
   */
  ngOnInit() {
    this.marginBottom = this.component.getParameters()?.marginBottom;
    this.stockTicker = this.component.getParameters()?.stockTicker;
    if (this.stockTicker) {
      this.stockTickerServiceService
        ?.getStockData$()
        ?.pipe(takeUntil(this.unsubscribe$))
        .subscribe(
          (data: StockDataDTO) => {
            if (data) {
              this.stockFeed = data?.GetStockQuoteListResult[0];
            }
            this.initPromoCards();
          },
          (error) => {
            logger.warn('error', error);
            this.initPromoCards();
          }
        );
    } else {
      this.initPromoCards();
    }

    this.cdr.detectChanges();
    logger.debug('ngOnInit complete');
  }

  /**
   * Init promo cards from CardContent documents
   */
  initPromoCards() {
    // This component has 3 document parameters
    this.initPromoCard('documentOne');
    if (!this.stockTicker) {
      this.initPromoCard('documentTwo');
    } else {
      if (this.stockFeed) {
        this.promoCards.push({
          title: this.translateService.instant('products.ticker-nyse'),
          hasThumbnail: false,
          hasStockPrice: true,
          stockPrice: `${symbols.get('USD')}${this.stockFeed?.TradePrice}`,
          currency: this.translateService.instant('products.ticker-usd'),
          stockAdditionalContent: `<div class="stock-value-change">${
            this.stockFeed?.Change
          } (${
            this.stockFeed?.PercChange
          }%)</div> <small>${this.translateService.instant(
            'products.ticker-msg'
          )}</small>`,
          linkBtnHref: this.translateService.instant('products.ticker-url'),
        });
      }
    }
    this.initPromoCard('documentThree');
  }

  /**
   * Create `PromoCardDetails` from cardContent document
   */
  initPromoCard(documentName: string) {
    const cardContent = this.getCardContent(documentName);
    if (cardContent) {
      logger.debug(cardContent);
      this.promoCards.push({
        title: cardContent.title,
        subTitle: cardContent.content,
        imageSrc: this.widenService.getWidenAssetUrl(
          cardContent.image?.widenAsset
        ),
        linkBtnHref: this.processLink(cardContent),
        isNewWindow:
          cardContent.linkCompound?.linkCollection[0]?.target === '_blank',
        externalLink: cardContent.linkCompound?.linkCollection[0]?.external,
        linkBtnLabel: cardContent.linkCompound?.linkCollection[0]?.displayText,
        signInRequired:
          cardContent.linkCompound?.linkCollection[0]?.signInLink &&
          !this.isLoggedIn,
      });
    }
  }

  /**
   * Process the different link types
   * @param cardContent card content
   */
  processLink(cardContent: CardContent): string {
    // Process link
    let link;
    if (cardContent.linkCompound?.linkCollection?.length > 0) {
      link = this.linkService.getCTALink(
        this.page,
        cardContent.linkCompound?.linkCollection[0],
        this.appStateService?.getHomePageUrl()
      );
    }
    logger.debug('link: ' + link);
    return link;
  }

  /**
   * Get card document from page model
   */
  getCardContent(documentName: string): CardContent {
    let document = this.component.getModels<DocumentModels>()[documentName];
    document = document && this.page.getContent(document);
    return document?.getData<CardContent>();
  }

  ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }
}
